/**
 * Pure component for the New User page (first page of the New User app)
 */

import React from 'react';
import classNames from 'classnames';
import iconChevronLeft from 'imported-assets/img/icon-chevron-left-blue.svg';
import { Range } from 'immutable';
import moment from 'moment';
import { shouldKeyTrigger } from 'phoenix2/utils/keyboard';
import PropTypes from 'prop-types';
import { VelocityTransitionGroup } from 'velocity-react';
import NewUserButton from './common/new-user-button';
import NewUserInput from './common/new-user-input';
import googleIcon from 'imported-assets/img/icon_google.png';
import azureIcon from 'imported-assets/img/icon_azure.png';

const BACK_BUTTON_THROTTLE_TIME_SEC = 0.2;

class BackToLoginButton extends React.Component {
    static displayName = 'BackButton';
    static propTypes = {
        onBack: PropTypes.func.isRequired,
        onSelectItem: PropTypes.func.isRequired,
    };

    constructor() {
        super();
    }

    render() {
        return (
            <div
                className="new-user-back-button"
                onClick={this.backClicked.bind(this, false)}
                onKeyDown={evt => shouldKeyTrigger(evt) && this.backClicked(true)}
                onMouseDown={evt => {
                    // Prevent focussing when clicked:
                    evt.preventDefault();
                }}
            >
                <img src={iconChevronLeft} alt="<" />
                {'Back to login'}
            </div>
        );
    }

    backClicked(withKeyboard = false) {
        const { onBack, onSelectItem } = this.props;

        // Tell analytics back was clicked:
        onSelectItem('nav bar', 'back', withKeyboard);
        onBack();
        this.setState({ disabled: true });

        // Disable the button for a short interval after being clicked, to
        // allow the animation to take place without another click happening.
        // Save the timeout so it can be cancelled on unmount if the back
        // button is removed
        this.disableTimeout = setTimeout(() => {
            this.setState({ disabled: false });
            this.disableTimeout = null;
        }, BACK_BUTTON_THROTTLE_TIME_SEC * 1000);
    }
}

/**
 * Wrapper around a select field (adds the styled orange arrow)
 * @param children the select field
 * @returns {*}
 * @constructor
 */
const SelectFieldWrapper = ({ children }) => (
    <div className="new-user-view-select">
        {children}
        <span className="new-user-view-select-arrow">
            <i className="new-user-view-select-arrow-icon fas fa-caret-down" />
        </span>
    </div>
);

SelectFieldWrapper.propTypes = {
    // The actual select field
    children: PropTypes.element.isRequired,
};

SelectFieldWrapper.displayName = 'SelectFieldWrapper';

/**
 * Wrapper around a validation message with box and Error icon
 * @param children the error message
 * @returns {*}
 * @constructor
 */
const ValidationMessage = ({ id, message }) => (
    <div className="new-user-view-validation-message" key={id}>
        <span className="new-user-view-validation-title">
            <i className="fas fa-exclamation-triangle" />
            Something&apos;s not quite right:
        </span>{' '}
        {message}
    </div>
);

ValidationMessage.propTypes = {
    // Validation message
    message: PropTypes.string.isRequired,

    // Id of the validation message - needed for key so animation knows when
    // a new message has appeared
    id: PropTypes.string.isRequired,
};

ValidationMessage.displayName = 'ValidationMessage';

export default class NewUserView extends React.Component {
    static displayName = 'NewUserView';

    static propTypes = {
        // Whether there was an error submitting the password handout request -
        // for example, the user entered an invalid name and DOB combination.
        // There is only one generic error message, so this is just a boolean
        error: PropTypes.bool,

        // When error is set, this value determines whether the error was a
        // result of the server throttling us.
        throttled: PropTypes.bool,

        // Whether to disable animations (on the validation message)
        disableAnimations: PropTypes.bool,

        // Callback for when the user submits the form
        onSubmit: PropTypes.func.isRequired,

        // Callback to turn off the error message after the user changes their
        // input after a validation error
        onResetError: PropTypes.func.isRequired,

        // Callback for when the user navigates with the forms back button.
        onBack: PropTypes.func.isRequired,
        onSelectItem: PropTypes.func.isRequired,

        // Whether we are in reset password mode
        passwordResetMode: PropTypes.bool,

        ssoProvider: PropTypes.string,
        ssoURL: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            // User's first name entry as they complete it
            firstName: '',
            // User's last name entry as they complete it
            lastName: '',
            // User's Date of Birth day as they complete it
            dobDay: null,
            // User's Date of Birth month as they complete it
            dobMonth: null,
            // User's Date of Birth year as they complete it
            dobYear: null,
        };
    }

    render() {
        const {
            disableAnimations,
            passwordResetMode,
            ssoProvider,
            ssoURL,
            error,
            throttled,
            onBack,
            onSelectItem,
        } = this.props;
        const { firstName, lastName } = this.state;

        // We disable the submit button if there is a validation message (it
        // disappears when the user changes something)
        const validationMessage = this._getValidationMessage();

        const disableSubmit = !this._allCompleted() || !this._validDate() || (error && !throttled);

        const validationMessageComp = disableAnimations ? (
            <div>{validationMessage}</div>
        ) : (
            <VelocityTransitionGroup
                enter={{ animation: 'slideDown' }}
                leave={{ animation: 'slideUp' }}
            >
                {validationMessage}
            </VelocityTransitionGroup>
        );

        const dobInput = (
            <div className="new-user-view-form-group">
                <label htmlFor="dobDay">Your date of birth:</label>
                <div className="new-user-view-dob">
                    {this._getDOBDay()}
                    {this._getDOBMonth()}
                    {this._getDOBYear()}
                </div>
            </div>
        );

        const makeSSOLoginButton = () => {
            const displayName = {
                google: 'Google',
                azure: 'Microsoft',
            };
            const icon = {
                google: googleIcon,
                azure: azureIcon,
            };
            return (
                <>
                    <div className="sso-text">
                        You can now login with {displayName[ssoProvider]} if you&apos;ve forgotten
                        your Sparx password
                    </div>
                    <a href={ssoURL} className="sso-link">
                        <div className="sso-login-button" tabIndex="0">
                            Log in <span className="hidden-on-mobile">to Sparx</span> with{' '}
                            {displayName[ssoProvider]}
                            <span className="hidden-on-mobile">
                                <img className="sso-provider-logo" src={icon[ssoProvider]} alt="" />
                            </span>
                        </div>
                    </a>
                    <div className="sso-or-container">
                        <hr />
                        <p className="sso-or-text">or</p>
                    </div>
                </>
            );
        };

        const ssoButton =
            passwordResetMode && ssoProvider && ssoProvider !== 'none'
                ? makeSSOLoginButton()
                : undefined;

        return (
            <div className="new-user-create-account-page">
                <div className="new-user-view-content">
                    <div className="new-user-view-box">
                        {ssoButton}
                        <div className="form-container">
                            <p className="new-user-header">
                                {passwordResetMode
                                    ? 'Reset your Sparx password'
                                    : 'Fill in your details below to create your account'}
                            </p>
                            <form onSubmit={evt => !disableSubmit && this._onSubmit(evt)}>
                                <NewUserInput
                                    name="first-name"
                                    value={firstName}
                                    label="Your first name:"
                                    placeholder="First name"
                                    handleChange={value => this._onFirstNameChanged(value)}
                                />
                                <NewUserInput
                                    name="last-name"
                                    value={lastName}
                                    label="Your last name:"
                                    placeholder="Last name"
                                    handleChange={value => this._onLastNameChanged(value)}
                                />
                                {dobInput}
                                {validationMessageComp}

                                <div className="new-user-view-form-button-group">
                                    <NewUserButton
                                        type="submit"
                                        value="Submit"
                                        disabled={disableSubmit}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                    <BackToLoginButton onBack={onBack} onSelectItem={onSelectItem} />
                </div>
            </div>
        );
    }

    /**
     * Determines whether all the fields are completed. The user must have
     * entered all components of the date of birth, and entered a first name and last name
     * with at least one character
     * @returns {boolean} true if valid, false otherwise
     * @private
     */
    _allCompleted() {
        const { firstName, lastName, dobDay, dobMonth, dobYear } = this.state;
        return (
            dobDay !== null &&
            dobMonth !== null &&
            dobYear !== null &&
            firstName !== null &&
            firstName.length >= 1 &&
            lastName !== null &&
            lastName.length >= 1
        );
    }

    /**
     * Gets the date the user enters as a moment.js date
     * @returns {moment.Moment}
     * @private
     */
    _getDate() {
        const { dobDay, dobMonth, dobYear } = this.state;
        return moment.utc({
            year: dobYear,
            month: dobMonth,
            day: dobDay,
        });
    }

    /**
     * Additional validation after the user has clicked the submit button.
     * Checks that the date is valid
     * @private
     */
    _validDate() {
        const momentDate = this._getDate();
        return momentDate.isValid();
    }

    /**
     * Given a day of the month, check it will create a valid date when combined
     * with the given month (and optionally year)
     * @param day the day of the month - integer from 1 to 31
     * @param month the month - integer from 0 (Jan) to 11 (Dec)
     * @param year (optional) integer - if provided, we can also check for
     * February 29th on leap year
     * @private
     */
    _validDay(day, month, year) {
        if (month === null) {
            return day <= 31;
        }

        let yearToTry = year;
        if (yearToTry === null) {
            // Use a leap year:
            yearToTry = 2016;
        }

        return moment
            .utc({
                year: yearToTry,
                month,
                day,
            })
            .isValid();
    }

    /**
     * Gets a React component for choosing a day in a date of birth
     * @returns {*}
     * @private
     */
    _getDOBDay() {
        const { dobDay, dobMonth, dobYear } = this.state;

        const value = dobDay === null ? 'Day' : dobDay;

        const dayString = day => (day < 10 ? '0' + day : day);

        return (
            <SelectFieldWrapper>
                <select
                    id="dobDay"
                    required={true}
                    name="dobDay"
                    value={value}
                    autoComplete="off"
                    onChange={evt => this._onDobDayChanged(evt)}
                    className={classNames({
                        ['new-user-view-dob-select-default']: dobDay === null,
                    })}
                >
                    <option disabled={true} value={null}>
                        Day
                    </option>
                    {Range(1, 32).map(day => {
                        // Only include days that are valid with the chosen month or
                        // year if already chosen:
                        if (this._validDay(day, dobMonth, dobYear)) {
                            return (
                                <option key={day} value={day}>
                                    {dayString(day)}
                                </option>
                            );
                        } else {
                            return null;
                        }
                    })}
                </select>
            </SelectFieldWrapper>
        );
    }

    /**
     * Gets a React component for choosing a month in a date of birth
     * @returns {*}
     * @private
     */
    _getDOBMonth() {
        const { dobMonth } = this.state;

        const value = dobMonth === null ? 'Month' : dobMonth;

        return (
            <SelectFieldWrapper>
                <select
                    name="dobMonth"
                    required={true}
                    value={value}
                    autoComplete="off"
                    onChange={evt => this._onDobMonthChanged(evt)}
                    className={classNames({
                        ['new-user-view-dob-select-default']: dobMonth === null,
                    })}
                >
                    <option disabled={true} value={null}>
                        Month
                    </option>
                    {moment.months().map((month, index) => (
                        <option key={index} value={index}>
                            {month}
                        </option>
                    ))}
                </select>
            </SelectFieldWrapper>
        );
    }

    /**
     * Gets a React component for choosing a year in a date of birth
     * @returns {*}
     * @private
     */
    _getDOBYear() {
        const { dobYear } = this.state;

        const value = dobYear === null ? 'Year' : dobYear;
        const currentYear = moment().year();

        return (
            <SelectFieldWrapper>
                <select
                    name="dobYear"
                    required={true}
                    value={value}
                    autoComplete="off"
                    onChange={evt => this._onDobYearChanged(evt)}
                    className={classNames({
                        ['new-user-view-dob-select-default']: dobYear === null,
                    })}
                >
                    <option disabled={true} value={null}>
                        Year
                    </option>
                    {Range(currentYear, currentYear - 100, -1).map((year, index) => (
                        <option key={index} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </SelectFieldWrapper>
        );
    }

    /**
     * Returns a dom element containing the validation message, or null if there
     * is no validation message to display
     * @returns {*}
     * @private
     */
    _getValidationMessage() {
        const { error, throttled } = this.props;

        const submissionErrorMessage = this._buildValidationMessage();

        if (error) {
            if (throttled) {
                return (
                    <ValidationMessage
                        id="throttled-error"
                        message={
                            'You have made too many requests recently. Please try again later.'
                        }
                    />
                );
            } else {
                return <ValidationMessage id="submission-error" message={submissionErrorMessage} />;
            }
        } else if (this._allCompleted() && !this._validDate()) {
            return <ValidationMessage id="validation-error" message={'Enter a valid date.'} />;
        }

        return null;
    }

    /**
     * Returns a validation message string
     * @returns {*}
     * @private
     */
    _buildValidationMessage() {
        const { passwordResetMode } = this.props;

        const firstLine = passwordResetMode
            ? 'The details you have entered aren\u2019t correct. '
            : 'Either the details you have entered aren\u2019t correct or you have already set up ' +
              'your account. ';

        return (
            firstLine +
            "You'll need to enter your details exactly as we have them in Sparx. Your teacher will be able to help you with this"
        );
    }

    /**
     * Called when the user changes the value in the firstName field to update
     * state. Also clears the validation message if applicable
     * @returns {*}
     * @private
     */
    _onFirstNameChanged(firstName) {
        this.setState({ firstName });
        this._onResetError();
    }

    /**
     * Called when the user changes the value in the lastName field to update
     * state. Also clears the validation message if applicable
     * @returns {*}
     * @private
     */
    _onLastNameChanged(lastName) {
        this.setState({ lastName });
        this._onResetError();
    }

    /**
     * Called when the user changes the value in the DOB day field to update
     * state. Also clears the validation message if applicable
     * @returns {*}
     * @private
     */
    _onDobDayChanged(evt) {
        this.setState({
            dobDay: evt.target.value,
        });
        this._onResetError();
    }

    /**
     * Called when the user changes the value in the DOB month field to update
     * state. Also clears the validation message if applicable
     * @returns {*}
     * @private
     */
    _onDobMonthChanged(evt) {
        const dobMonth = evt.target.value;
        const dobYear = this.state.dobYear;
        let dobDay = this.state.dobDay;

        // Reset the day if it is incompatible with newly chosen month:
        if (dobDay !== null && !this._validDay(dobDay, dobMonth, dobYear)) {
            dobDay = null;
        }

        this.setState({
            dobDay,
            dobMonth,
        });
        this._onResetError();
    }

    /**
     * Called when the user changes the value in the DOB year field to update
     * state. Also clears the validation message if applicable
     * @returns {*}
     * @private
     */
    _onDobYearChanged(evt) {
        const dobMonth = this.state.dobMonth;
        const dobYear = evt.target.value;
        let dobDay = this.state.dobDay;

        // Reset the day if it is incompatible with newly chosen year (e.g.
        // the date was 29 February and we change the year that is not a leap
        // year):
        if (dobDay !== null && !this._validDay(dobDay, dobMonth, dobYear)) {
            dobDay = null;
        }

        this.setState({
            dobDay,
            dobYear: evt.target.value,
        });
        this._onResetError();
    }

    /**
     * Called when the user submits the form
     * @returns {*}
     * @private
     */
    _onSubmit(evt) {
        const { firstName, lastName } = this.state;
        const { onSubmit } = this.props;

        // Stop the browser posting the form
        evt.preventDefault();

        this.setState({
            validationMessage: null,
        });
        onSubmit(firstName.trim(), lastName.trim(), this._getDate().toISOString(true));
    }

    /**
     * Clears the validation message
     * @returns {*}
     * @private
     */
    _onResetError() {
        if (this.props.error) {
            this.props.onResetError();
        }
    }
}
